import React, { useState, useEffect, useMemo } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import {
  Navigation,
  Main,
  Footer,
  About,
  FadeIn,
} from "./components";
import AdminDashboard from './components/AdminDashboard';
import './index.scss';

function App() {
    const [mode, setMode] = useState<'light' | 'dark'>('dark');

    const theme = useMemo(
        () =>
            createTheme({
                palette: {
                    mode: mode,
                    primary: {
                        main: '#1B75BC',
                        light: '#4A90D9',
                        dark: '#0D5AA7',
                        contrastText: '#ffffff',
                    },
                    secondary: {
                        main: '#FDB813',
                        light: '#FFD54F',
                        dark: '#F57F17',
                        contrastText: '#000000',
                    },
                    error: {
                        main: '#DC2626',
                        light: '#EF4444',
                        dark: '#B91C1C',
                    },
                    warning: {
                        main: '#FDB813',
                        light: '#FFD54F',
                        dark: '#F57F17',
                    },
                    info: {
                        main: '#1B75BC',
                        light: '#4A90D9',
                        dark: '#0D5AA7',
                    },
                    success: {
                        main: '#059669',
                        light: '#10B981',
                        dark: '#047857',
                    },
                    background: {
                        default: mode === 'dark' ? '#0d1116' : '#f8f9fa',
                        paper: mode === 'dark' ? '#161b22' : '#ffffff',
                    },
                    text: {
                        primary: mode === 'dark' ? '#e6edf3' : '#1a1a2e',
                        secondary: mode === 'dark' ? '#b0b8c4' : '#4a5568',
                        disabled: mode === 'dark' ? '#7d8590' : '#a0aec0',
                    },
                    divider: mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
                },
                typography: {
                    fontFamily: '"Lato", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                    h1: {
                        fontWeight: 700,
                    },
                    h2: {
                        fontWeight: 700,
                    },
                    h3: {
                        fontWeight: 600,
                    },
                    h4: {
                        fontWeight: 600,
                    },
                    h5: {
                        fontWeight: 600,
                    },
                    h6: {
                        fontWeight: 600,
                    },
                    button: {
                        textTransform: 'none',
                        fontWeight: 600,
                    },
                },
                components: {
                    MuiButton: {
                        styleOverrides: {
                            root: {
                                borderRadius: 8,
                                padding: '8px 16px',
                                minHeight: 44,
                            },
                            contained: {
                                boxShadow: mode === 'dark' 
                                    ? '0 4px 6px rgba(0,0,0,0.3)' 
                                    : '0 4px 6px rgba(0,0,0,0.1)',
                            },
                        },
                    },
                    MuiTextField: {
                        styleOverrides: {
                            root: {
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 8,
                                },
                            },
                        },
                    },
                    MuiOutlinedInput: {
                        styleOverrides: {
                            root: {
                                borderRadius: 8,
                            },
                        },
                    },
                    MuiCard: {
                        styleOverrides: {
                            root: {
                                borderRadius: 12,
                                boxShadow: mode === 'dark'
                                    ? '0 4px 6px rgba(0,0,0,0.4)'
                                    : '0 4px 6px rgba(0,0,0,0.1)',
                            },
                        },
                    },
                    MuiPaper: {
                        styleOverrides: {
                            root: {
                                borderRadius: 12,
                            },
                        },
                    },
                    MuiAppBar: {
                        styleOverrides: {
                            root: {
                                backgroundColor: mode === 'dark' ? '#0d1116' : '#f8f9fa',
                                color: mode === 'dark' ? '#e6edf3' : '#1a1a2e',
                            },
                        },
                    },
                },
            }),
        [mode],
    );

    const handleModeChange = () => {
        setMode((prevMode) => prevMode === 'dark' ? 'light' : 'dark');
    };

    useEffect(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    }, []);

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <Router>
                <div className={`main-container ${mode === 'dark' ? 'dark-mode' : 'light-mode'}`}>
                    <Routes>
                        <Route path="/" element={
                            <>
                                <FadeIn transitionDuration={700}>
                                    <Navigation parentToChild={{ mode }} modeChange={handleModeChange} />
                                    <Main />
                                    <About />
                                </FadeIn>
                                <Footer />
                            </>
                        } />
                        <Route path="/admin" element={<AdminDashboard />} />
                    </Routes>
                </div>
            </Router>
        </ThemeProvider>
    );
}

export default App;
